import { useState, useEffect } from 'react';
import classNames from 'classnames';
// @ts-ignore
import { createFilter, Option, Options } from 'react-select';

/* Components */
import { ELEMENT_SIZE, SelectField, Label, InputField } from 'ht-styleguide';
import { WarningHintIcon } from '@components/UI/Messages';

/* Types */
import { ExtendedSelectOptions, SelectArrayOptions } from 'types/base.types';
import { CustomDropdownValues, ModelAnswer, QuestionDeviceProps } from 'types/qa.types';

/* Constants & Utis */
import { CUSTOM_DROPDOWN_OPTIONS } from '@scenes/AddSkuPage/addsku.constants';
import { getDeviceModelOptions, getDeviceMakeOptions, getSelectedModel, getModelQuestion, isStringType } from './utils';

/* Styles */
import styles from './prequestioncheckbox.module.scss';

const DEFAULT_VALUES = {
  PLACEHOLDER: 'Select',
} as const;

type InitialState = {
  hintVisible: boolean;
  isModelDisabled: boolean;
  showInput: boolean;
  showModelInput: boolean;
  models: SelectArrayOptions | null;
  filteredModels: SelectArrayOptions | null;
  placeholder: string;
  allOptions: SelectArrayOptions;
};

const INITIAL_STATE: InitialState = {
  hintVisible: false,
  isModelDisabled: true,
  showInput: false,
  showModelInput: false,
  models: null,
  filteredModels: null,
  placeholder: DEFAULT_VALUES.PLACEHOLDER,
  allOptions: [],
};

const QuestionDevice = ({
  question,
  value,
  modelValue,
  onMakeDropdownChange,
  onModelDropdownChange,
  onMakeInputChange,
  onModelInputChange,
  makeInputValue,
  modelInputValue,
  error,
  sku,
  onIconClick,
}: QuestionDeviceProps) => {
  // get options
  const questionAnswers = question.answers as ModelAnswer[];
  const options = getDeviceMakeOptions(question, sku);
  const modelQuestion = getModelQuestion({ answers: questionAnswers });
  const [state, setState] = useState(INITIAL_STATE);
  const makeQuestion = question.textDirect;
  const { hintVisible, isModelDisabled, showInput, showModelInput, models, filteredModels, placeholder, allOptions } = state;
  const hintIconStyles = classNames(styles.answerHintIcon, styles.answerHintIconInput);

  // push custom options 'other' & 'idk'
  options.push(CUSTOM_DROPDOWN_OPTIONS.OTHER);
  options.push(CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW);

  useEffect(() => {
    const selected = getSelectedModel({ questionAnswers, valueId: value });
    const products = selected && selected.productQuestion;
    const modelOptions = getDeviceModelOptions({ products });
    const timeoutId = setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        filteredModels: modelOptions,
        models: modelOptions,
        isModelDisabled: !value,
        allOptions: options,
        modelVal: modelValue,
      }));
      if (value === CustomDropdownValues.negOne) {
        setState(prevState => ({
          ...prevState,
          isModelDisabled: true,
          showModelInput: false,
        }));
      }
      if (hintVisible) {
        setState(prevState => ({
          ...prevState,
          hintVisible: !hintVisible,
        }));
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [value, hintVisible, modelValue, options, questionAnswers]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isStringType(modelValue)) {
        setState(prevState => ({
          ...prevState,
          showModelInput: true,
        }));
      }
      if (isStringType(value)) {
        setState(prevState => ({
          ...prevState,
          showInput: true,
          showModelInput: true,
        }));
      }
      if (value === CustomDropdownValues.negOne) {
        setState(prevState => ({
          ...prevState,
          isModelDisabled: true,
          placeholder: CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.label,
        }));
      } else {
        setState(prevState => ({
          ...prevState,
          placeholder: DEFAULT_VALUES.PLACEHOLDER,
        }));
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [hintVisible, modelValue, options, questionAnswers, value]);

  const handleMakeDropdownChange = (v: CustomDropdownValues) => {
    const isOtherOption = v === CUSTOM_DROPDOWN_OPTIONS.OTHER.value;
    const isIDKOption = v === CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.value;

    if (isOtherOption || isIDKOption) {
      if (isOtherOption) {
        setState(prevState => ({
          ...prevState,
          showModelInput: true,
          showInput: true,
          placeholder: CUSTOM_DROPDOWN_OPTIONS.OTHER.label,
        }));
      }
      if (isIDKOption) {
        setState(prevState => ({
          ...prevState,
          placeholder: CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.label,
          isModelDisabled: true,
        }));
      }
    } else {
      setState(prevState => ({
        ...prevState,
        isModelDisabled: false,
        showModelInput: false,
        showInput: false,
        placeholder: DEFAULT_VALUES.PLACEHOLDER,
      }));
    }
    const selected = getSelectedModel({ questionAnswers, valueId: v });
    const products = selected && selected.productQuestion;
    const modelOptions = getDeviceModelOptions({ products });
    setState(prevState => ({
      ...prevState,
      models: modelOptions,
    }));
    onMakeDropdownChange(v);
  };

  const handleModelDropdownChange = (v: CustomDropdownValues) => {
    const isOtherOption = v === CUSTOM_DROPDOWN_OPTIONS.OTHER.value;
    if (isOtherOption) {
      setState(prevState => ({
        ...prevState,
        showModelInput: true,
      }));
    }
    onModelDropdownChange(v);
  };

  const handleIconClick = () => {
    setState(prevState => ({
      ...prevState,
      showModelInput: false,
      showInput: false,
      isModelDisabled: true,
      placeholder: DEFAULT_VALUES.PLACEHOLDER,
    }));
    onIconClick();
  };

  /*
    * filterOption is a prop in react-select. Setting it to true disables the custom no options message and enables us
      to customize the options to be shown when no search results are found. (in type ahead)
    * Note that the no options message is pushed as a custom 'option' object where we can also add an 'isDisabled' prop that will make this non-interactive.
  */
  const filterAllOptions = (input: string, optionList: ExtendedSelectOptions<string | number | boolean>[] | null, stateValue: string) => {
    const filteredOptions = (optionList ?? []).filter(
      (o: ExtendedSelectOptions<string | number | boolean>) =>
        // @ts-ignore
        !(o?.searchable === false) && createFilter({})({ ...o, data: {} }, input),
    );

    const noOptions = filteredOptions.length === 0;
    if (noOptions) {
      filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.NO_OPTIONS);
    }
    filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.OTHER);
    filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW);

    setState(prevState => ({
      ...prevState,
      [stateValue]: filteredOptions,
    }));
  };
  // const filterAllOptions = (input: string, optionList: SelectArrayOptions | null, stateValue: string) => {
  //   // @ts-ignore
  //   const filteredOptions = (optionList ?? []).filter(o => !(o.searchable === false) && filterOption(o, input));
  //   const noOptions = filteredOptions.length === 0;
  //   if (noOptions) {
  //     filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.NO_OPTIONS);
  //   }
  //   filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.OTHER);
  //   filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW);
  //
  //   setState(prevState => ({
  //     ...prevState,
  //     [stateValue]: filteredOptions,
  //   }));
  // };

  /**
   * React-select v5 update.
   * @remarks
   *    need to revisit this
   *
   * @param {} option
   * @param {Options<string>} selectValue
   * @returns {boolean}
   */
  const isOptionSelected = (_option: Option, _selectValue: Options<unknown>) => Boolean(modelValue);

  /* Custom filter option for user filtering */
  const filterOptionFn = (...args: [{ label: string }, string]) => {
    const [{ label }, searchKey] = args;
    /* Lowercase the args */
    const lowerLabel = label?.toLowerCase() || '';
    const lowerSearchKey = searchKey?.toLowerCase() || '';
    /* Does the user input match the beginning of the option label */
    const filteredLabels = lowerLabel.startsWith(lowerSearchKey);
    /* We need to allow the custom options (i don't know, othe) to pass thru */
    const customOptions = Object.values(CUSTOM_DROPDOWN_OPTIONS).map(group => group.label.toLowerCase());

    /* We found a match or custom options */
    if (filteredLabels || customOptions.includes(lowerLabel)) {
      return true;
    }

    return false;
  };

  return (
    <div className={styles.answer}>
      <div>
        {showInput ? (
          <>
            <Label label={makeQuestion} elementSize={ELEMENT_SIZE.LARGE} />
            <InputField
              showError={false}
              type="text"
              value={makeInputValue}
              onChange={onMakeInputChange}
              error={error.make}
              iconName="v2-close-icon"
              iconOnClick={handleIconClick}
              autoFocus={showInput}
            />
          </>
        ) : (
          <SelectField
            placeholder="Select"
            options={allOptions}
            filterOption={filterOptionFn}
            onInputChange={(input: string) => filterAllOptions(input, options, 'allOptions')}
            label={makeQuestion}
            value={value || ''}
            onChange={({ value: v }) => handleMakeDropdownChange(v)}
            elementSize={ELEMENT_SIZE.MEDIUM}
            clearable={false}
            searchable
            error={error.make}
          />
        )}
      </div>
      <div className="marginTop-small1">
        {showModelInput ? (
          <>
            <Label label={modelQuestion} elementSize={ELEMENT_SIZE.LARGE} />
            <InputField type="text" value={modelInputValue as string} onChange={onModelInputChange} error={error.model} />
          </>
        ) : (
          <SelectField
            placeholder={placeholder}
            options={models || undefined}
            filterOption={filterOptionFn}
            onInputChange={(input: string) => filterAllOptions(input, filteredModels, 'models')}
            label={modelQuestion}
            value={modelValue || ''}
            isOptionSelected={isOptionSelected}
            onChange={({ value: v }) => handleModelDropdownChange(v)}
            elementSize={ELEMENT_SIZE.MEDIUM}
            searchable
            error={error.model}
            clearable={false}
            isDisabled={isModelDisabled}
          />
        )}
      </div>
      <WarningHintIcon content={question.hint} hintIconStyles={hintIconStyles} />
    </div>
  );
};

export default QuestionDevice;

/*
  @remarks
  Context:
    * There are two questions (currently) that a user answers - make, and, model of their device.
    * The questions are presented as dropdowns initially and if user picks "other" in make question,
      we render input fields for both questions.
    * We need to send back {answerId, make, model} for the questions to the BE
  The question object coming back from the api looks something like this:

  @example
  questions:[
    0: {
      ...
      id: 5428
      inputType: "device"
      textDirect: "What MAKE is your computer?"
      text: "Apple"
      answers: [
        0: {
          ...
          id: 13272
          productQuestion: {
            id: 9
            text: "What MODEL is your computer?"
            answers: [
              0: {id: 24, name: "Apple TV"}
              1: {id: 135, name: "HomePod"}
              2: {id: 166, name: "Laptop"}
              3: {id: 167, name: "Desktop"}
            ]
          }
        1: {id: 13278, text: "Google"........}
      ]
    }
    1: {.....}
    2: {.....}
  ]
*/
